import { api, apiWithMeta } from "./client";
import type {
  Chain,
  ChainAccess,
  ChainSummary,
  ChainType,
  Connection,
  ConnectionDirection,
  ConnectionLifetime,
  ConnectionMassState,
  PresenceBySystem,
  Signature,
  StructureKind,
  SystemActivity,
  SystemHostility,
  SystemOwner,
  SystemStructure,
} from "../types/chain";

export function listChains(): Promise<ChainSummary[]> {
  return api<ChainSummary[]>("/chains");
}

export interface GetChainResult {
  chain: Chain | null;
  lastModified: string | null;
  notModified: boolean;
}

export async function getChain(
  id: string,
  ifModifiedSince?: string | null,
): Promise<GetChainResult> {
  const init: RequestInit | undefined = ifModifiedSince
    ? { headers: { "if-modified-since": ifModifiedSince } }
    : undefined;
  const res = await apiWithMeta<Chain>(
    `/chains/${encodeURIComponent(id)}`,
    init,
  );
  return {
    chain: res.data,
    lastModified: res.headers.get("last-modified"),
    notModified: res.status === 304,
  };
}

export function getPresence(chainId: string): Promise<PresenceBySystem> {
  return api<{ presence: PresenceBySystem }>(
    `/chains/${encodeURIComponent(chainId)}/presence`,
  ).then((r) => r.presence);
}

export interface CreateChainBody {
  name: string;
  root_system_id: number;
  access: ChainAccess;
  chain_type?: ChainType;
}

export function createChain(body: CreateChainBody): Promise<ChainSummary> {
  return api<ChainSummary>("/chains", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface UpdateChainBody {
  name?: string;
  root_system_id?: number;
  access?: ChainAccess;
}

export function updateChain(
  id: string,
  body: UpdateChainBody,
): Promise<ChainSummary> {
  return api<ChainSummary>(`/chains/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function deleteChain(id: string): Promise<void> {
  return api<void>(`/chains/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function patchLayout(
  chainId: string,
  positions: Record<string, { x: number; y: number }>,
): Promise<void> {
  return api<void>(`/chains/${encodeURIComponent(chainId)}/layout`, {
    method: "PATCH",
    body: JSON.stringify({ positions }),
  });
}

export interface ConnectionPatch {
  wh_type?: string;
  lifetime?: ConnectionLifetime;
  mass?: ConnectionMassState;
  direction?: ConnectionDirection;
}

export function patchConnection(
  chainId: string,
  connectionId: string,
  patch: ConnectionPatch,
): Promise<Connection> {
  return api<Connection>(
    `/chains/${encodeURIComponent(chainId)}/connections/${encodeURIComponent(connectionId)}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export function removeSystem(
  chainId: string,
  systemId: string,
): Promise<void> {
  return api<void>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}`,
    { method: "DELETE" },
  );
}

export function routeToSystem(
  chainId: string,
  systemId: string,
): Promise<void> {
  return api<void>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/route`,
    { method: "POST" },
  );
}

export function putSignatures(
  chainId: string,
  systemId: string,
  signatures: Signature[],
  lazyDelete: boolean = true,
): Promise<Signature[]> {
  return api<Signature[]>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/signatures`,
    {
      method: "PUT",
      body: JSON.stringify({ signatures, lazy_delete: lazyDelete }),
    },
  );
}

export interface SystemAnnotationsPatch {
  hostility?: SystemHostility | null;
  activity?: SystemActivity | null;
  notes?: string | null;
  alias?: string | null;
}

export interface SystemAnnotationsResult {
  hostility: SystemHostility | null;
  activity: SystemActivity | null;
  notes: string | null;
  alias: string | null;
  annotations_updated_at: string | null;
}

export function patchSystemAnnotations(
  chainId: string,
  systemId: string,
  patch: SystemAnnotationsPatch,
): Promise<SystemAnnotationsResult> {
  return api<SystemAnnotationsResult>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/annotations`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export interface CreateStructureBody {
  kind: StructureKind;
  name: string;
}

export function createStructure(
  chainId: string,
  systemId: string,
  body: CreateStructureBody,
): Promise<SystemStructure> {
  return api<SystemStructure>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/structures`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export interface StructurePatch {
  kind?: StructureKind;
  name?: string;
}

export function patchStructure(
  chainId: string,
  systemId: string,
  structureId: number,
  patch: StructurePatch,
): Promise<SystemStructure> {
  return api<SystemStructure>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/structures/${structureId}`,
    { method: "PATCH", body: JSON.stringify(patch) },
  );
}

export function deleteStructure(
  chainId: string,
  systemId: string,
  structureId: number,
): Promise<void> {
  return api<void>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/structures/${structureId}`,
    { method: "DELETE" },
  );
}

export interface CreateOwnerBody {
  entity_type: "corp" | "alliance";
  entity_id: number;
}

export function createOwner(
  chainId: string,
  systemId: string,
  body: CreateOwnerBody,
): Promise<SystemOwner> {
  return api<SystemOwner>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/owners`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

export function deleteOwner(
  chainId: string,
  systemId: string,
  entityType: "corp" | "alliance",
  entityId: number,
): Promise<void> {
  return api<void>(
    `/chains/${encodeURIComponent(chainId)}/systems/${encodeURIComponent(systemId)}/owners/${entityType}/${entityId}`,
    { method: "DELETE" },
  );
}

export interface EsiEntityCandidate {
  id: number;
  name: string;
}

export function resolveEsiEntities(
  type: "corporation" | "alliance",
  q: string,
): Promise<EsiEntityCandidate[]> {
  return api<{ candidates: EsiEntityCandidate[] }>(
    `/esi/entities?type=${encodeURIComponent(type)}&q=${encodeURIComponent(q)}`,
  ).then((r) => r.candidates);
}
